import { useState } from "react";
import { api, formatError, BRAND } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

const registrantTypes = [
  "Individual", "Business or corporate team", "Childcare center", "School",
  "Church or faith-based organization", "Healthcare organization",
  "Community or nonprofit organization", "Other",
];
const trainingOptions = [
  "Adult CPR/AED", "Pediatric CPR/AED", "First Aid",
  "CPR, AED, and First Aid combination", "Childcare-focused training",
  "Workplace safety training", "Not sure—please help me choose", "Other",
];
const participantCounts = ["1 person", "2–5 people", "6–10 people", "11–20 people", "21 or more people"];
const formatOptions = ["At the KenMarq training location", "On-site at the client's location", "Either option"];

export default function GroupTraining() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    organization: "", registrant_type: "", org_type: "",
    training_requested: "", participants: "", preferred_format: "",
    preferred_date: "", city_state: "", how_soon: "", additional_notes: "",
    contact_permission: false,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_permission) { toast.error("Please allow us to contact you"); return; }
    setSubmitting(true);
    try {
      await api.post("/inquiries", form);
      setSubmitted(true);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <CheckCircle2 className="h-16 w-16 text-brand-teal mx-auto" />
        <h1 className="font-display text-4xl font-extrabold text-brand-navy mt-6 tracking-tight">Thank you!</h1>
        <p className="mt-4 text-slate-600 leading-relaxed">
          We've received your training inquiry. Our team will review the details and reach out within one business day with a tailored proposal. A confirmation email is on the way.
        </p>
      </div>
    );
  }

  return (
    <div>
      <section className="bg-brand-navy text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{ backgroundImage: `url(${BRAND.corporateImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <span className="font-script text-3xl text-brand-amber">For teams</span>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight mt-2 max-w-3xl">Group & Corporate Training</h1>
          <p className="mt-4 max-w-2xl text-white/80 leading-relaxed">
            Need training for your entire team? Tell us about your organization, preferred dates, and number of participants, and we'll help you plan the right class — at your location or ours.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <h2 className="font-display text-2xl font-bold text-brand-navy tracking-tight">Private training for:</h2>
          <ul className="mt-4 space-y-2 text-slate-700">
            {["Childcare centers", "Schools", "Healthcare & human-service organizations", "Corporate offices", "Churches", "Nonprofit organizations", "Community groups", "Small businesses", "Professional teams"].map((x) => (
              <li key={x} className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-brand-teal shrink-0" /> {x}</li>
            ))}
          </ul>
        </div>

        <form onSubmit={submit} className="lg:col-span-8 bg-white border border-slate-200 rounded-3xl p-8 space-y-6" data-testid="inquiry-form">
          <div className="grid md:grid-cols-2 gap-4">
            <F label="First name *" required value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testid="inq-firstname" />
            <F label="Last name *" required value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} testid="inq-lastname" />
            <F label="Email *" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="inq-email" />
            <F label="Phone *" required value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="inq-phone" />
            <F label="Company or organization" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} testid="inq-org" />
            <F label="Type of business/organization" value={form.org_type} onChange={(v) => setForm({ ...form, org_type: v })} testid="inq-orgtype" />
          </div>
          <SelectField label="Registering as *" value={form.registrant_type} onChange={(v) => setForm({ ...form, registrant_type: v })} options={registrantTypes} testid="inq-regtype" />
          <SelectField label="Training requested *" value={form.training_requested} onChange={(v) => setForm({ ...form, training_requested: v })} options={trainingOptions} testid="inq-training" />
          <div className="grid md:grid-cols-2 gap-4">
            <SelectField label="Estimated participants *" value={form.participants} onChange={(v) => setForm({ ...form, participants: v })} options={participantCounts} testid="inq-participants" />
            <SelectField label="Preferred format *" value={form.preferred_format} onChange={(v) => setForm({ ...form, preferred_format: v })} options={formatOptions} testid="inq-format" />
            <F label="Preferred date / date range" value={form.preferred_date} onChange={(v) => setForm({ ...form, preferred_date: v })} testid="inq-date" />
            <F label="City & state where training is needed" value={form.city_state} onChange={(v) => setForm({ ...form, city_state: v })} testid="inq-location" />
            <F label="How soon is training needed?" value={form.how_soon} onChange={(v) => setForm({ ...form, how_soon: v })} testid="inq-timeline" />
          </div>
          <div>
            <Label className="text-brand-navy font-semibold">Additional questions or special requests</Label>
            <Textarea rows={4} value={form.additional_notes} onChange={(e) => setForm({ ...form, additional_notes: e.target.value })} className="mt-1 bg-brand-surface border-slate-200 rounded-lg" data-testid="inq-notes" />
          </div>
          <div className="flex items-start gap-3 p-4 bg-brand-surface rounded-xl">
            <Checkbox checked={form.contact_permission} onCheckedChange={(v) => setForm({ ...form, contact_permission: !!v })} data-testid="inq-permission" />
            <label className="text-sm text-slate-700 leading-relaxed">I give KenMarq Safety Training permission to contact me by email or phone about my request. *</label>
          </div>
          <Button type="submit" disabled={submitting} className="w-full bg-brand-coral hover:bg-brand-coral-dark text-white rounded-full py-6 font-bold text-base btn-lift" data-testid="inq-submit">
            {submitting ? "Sending..." : "Request Group Training Quote"}
          </Button>
        </form>
      </section>
    </div>
  );
}

function F({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <Label className="text-brand-navy font-semibold">{label}</Label>
      <Input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 bg-brand-surface border-slate-200 rounded-lg" data-testid={testid} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-brand-navy font-semibold">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1 bg-brand-surface border-slate-200 rounded-lg" data-testid={testid}>
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
